import { allowLocalModels } from '../platform';

export type Agent = {
  id: 'lmstudio' | 'openai' | 'anthropic' | 'openrouter';
  name: string;
  icon: string;
  color: string;
  model: string;
  models: string[];
  description: string;
};

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  time: string;
  recommendations?: { id: string; name: string; why: string }[];
};

export type ChatFile = {
  id: string;
  name: string;
  type: 'file' | 'folder';
  content?: string;
};

export type Chat = {
  id: string;
  title: string;
  agentId: Agent['id'];
  model: string;
  baseUrl: string;
  context: string;
  files: ChatFile[];
  messages: ChatMessage[];
  updatedAt: string;
};

export const AGENTS: Agent[] = [
  {
    id: 'lmstudio',
    name: 'LM Studio',
    icon: 'ri-computer-line',
    color: '#00B894',
    model: '',
    models: [],
    description: 'Local OpenAI-compatible models on your machine',
  },
  {
    id: 'anthropic',
    name: 'Claude',
    icon: 'ri-terminal-box-line',
    color: '#6C5CE7',
    model: 'claude-sonnet-5',
    models: ['claude-sonnet-5', 'claude-opus-5', 'claude-fable-5', 'claude-haiku-4-5'],
    description: 'Anthropic Claude for product and tech reasoning',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    icon: 'ri-sparkling-2-line',
    color: '#FDCB6E',
    model: 'gpt-5.6',
    models: ['gpt-5.6', 'gpt-5.6-terra', 'gpt-5.6-luna'],
    description: 'OpenAI GPT for general composition help',
  },
  {
    id: 'openrouter',
    name: 'Hosted',
    icon: 'ri-cloud-line',
    color: '#E17055',
    model: '',
    models: [],
    description: 'Cloud models billed from your credit wallet',
  },
];

export type ModelGroup = { label: string; ids: string[] };

export function groupOpenRouterModels(ids: string[]): ModelGroup[] {
  const order: string[] = [];
  const map = new Map<string, string[]>();
  for (const id of ids) {
    const slash = id.indexOf('/');
    const label = slash > 0 ? id.slice(0, slash) : 'other';
    if (!map.has(label)) {
      map.set(label, []);
      order.push(label);
    }
    map.get(label)!.push(id);
  }
  return order.map((label) => ({ label, ids: map.get(label)! }));
}

export function extraModelOption(current: string, listed: string[]): string | null {
  const id = current.trim();
  if (!id || listed.includes(id)) return null;
  return id;
}

export function listedAgents(desktop: boolean): Agent[] {
  return desktop ? AGENTS : AGENTS.filter((a) => a.id !== 'lmstudio');
}

export function defaultAgentId(desktop: boolean): Agent['id'] {
  return desktop ? 'lmstudio' : 'openrouter';
}

export function listedAgentId(agentId: Agent['id'] | undefined, desktop: boolean): Agent['id'] {
  const listed = listedAgents(desktop);
  if (agentId && listed.some((a) => a.id === agentId)) return agentId;
  return defaultAgentId(desktop);
}

export function emptyChat(agentId: Agent['id'] = listedAgentId(undefined, allowLocalModels())): Chat {
  const agent = AGENTS.find((a) => a.id === agentId) ?? AGENTS.find((a) => a.id === listedAgentId(undefined, allowLocalModels())) ?? AGENTS[0];
  return {
    id: `c${Date.now()}`,
    title: 'New chat',
    agentId: agent.id,
    model: agent.model,
    baseUrl: 'http://127.0.0.1:1234/v1',
    context: '',
    files: [],
    messages: [],
    updatedAt: 'now',
  };
}
